import { sortTopics } from 'src/utils/schedule';
import { replaceCurriculum } from 'src/store/curriculum';
import { updateActivitySchedule } from 'src/store/activity';
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import _ from 'lodash';

import { RootState } from 'src/store/index';
import { weekSelectorByClassId } from 'src/store/model/week_topic';
import { selector as classTopicContentSelector } from 'src/store/model/class_topic_content';
import { selector as userAssignmentFileSelector } from 'src/store/model/user_assignment_file';
import {
  selectClassUserByClassId,
  userCoursesSelector,
} from 'src/store/model/class_user';
import { MenuData } from 'src/components/custom/Menu/context';
import dayjs from 'src/libs/dayjs';
import { getExpiredDate } from 'src/utils/helper';
import { Key } from 'react';
import { classByIdSelector } from 'src/store/model/classes';
import { hasStaffCourseAccess } from 'src/utils/courseCapabilities';
import { PATH } from 'src/constants/app';

export type State = {
  loading: boolean;
};

export const adapter = createEntityAdapter<Model.ClassTopic>({
  selectId: (model) => model.id,
  sortComparer: (prev, next) => {
    return dayjs(prev.created_at).isBefore(next.created_at) ? -1 : 1;
  },
});

const { reducer, actions } = createSlice({
  name: 'class_topic',
  initialState: adapter.getInitialState<State>({ loading: false }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.ClassTopic[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.ClassTopic>) {
      adapter.upsertOne(state, action);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    updateModuleQuizStatus(
      state,
      action: PayloadAction<{
        class_topic: Model.ClassTopic;
        checked: boolean;
      }>,
    ) {
      const { checked, class_topic } = action.payload;
      adapter.updateOne(state, {
        id: class_topic.id,
        changes: { has_submitted_questions: checked },
      });
    },
    updateHomeworkStatus(
      state,
      action: PayloadAction<{
        class_topic: Model.ClassTopic;
        checked: boolean;
      }>,
    ) {
      const { checked, class_topic } = action.payload;
      adapter.updateOne(state, {
        id: class_topic.id,
        changes: { has_submitted_homeworks: checked },
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateActivitySchedule, (state, { payload }) => {
      adapter.updateOne(state, {
        id: payload.id,
        changes: {
          start_at: payload.start_at,
          ...(payload.end_at !== undefined ? { end_at: payload.end_at } : {}),
        },
      });
    });
    builder.addCase(replaceCurriculum, (state, action) => {
      const { course, lessons } = action.payload.snapshot;
      adapter.removeMany(
        state,
        Object.values(state.entities)
          .filter((item) => item?.class_id === course.id)
          .map((item) => item!.id),
      );
      adapter.upsertMany(state, lessons);
    });
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.class_topic,
);

/**
 * Merge data class topic model into class topic of weeks, because when we call api class/id only some fields in
 * class_topic appeared
 * */
export const topicsSelectorByClassSeparateByWeek = createSelector(
  (state: RootState) => selectClassTopicsWithContent(state),
  (state: RootState) => state.ui.app.me?.id,
  (state: RootState, class_id: number | string | undefined) =>
    weekSelectorByClassId(state, class_id),
  (class_topics, user_id, weeks) => {
    return weeks.map((week) => {
      const class_topics_with_content = (week.class_topics || []).map(
        (class_topic_no_content) => {
          const class_topic_with_content = class_topics.find(
            ({ id }) => class_topic_no_content.id === id,
          );

          return {
            ...class_topic_no_content,
            ...class_topic_with_content,
            user_assignment_files:
              (
                class_topic_with_content || class_topic_no_content
              ).user_assignment_files?.filter(
                (file) => file.user_id === user_id,
              ) || [],
          };
        },
      );

      return {
        ...week,
        class_topics: sortTopics(class_topics_with_content),
      };
    });
  },
);

export const selectCurriculumPublished = createSelector(
  (state: RootState, class_id: number | string | undefined) =>
    topicsSelectorByClassSeparateByWeek(state, class_id),
  (state: RootState, class_id: number | string | undefined) =>
    hasStaffCourseAccess(classByIdSelector(state, String(class_id))),
  (weeks, staffAccess) => {
    if (staffAccess) return weeks;
    const publishedWeeks = weeks.filter((w) => w.is_published);

    return publishedWeeks.map((w) => {
      const publishedClassTopics = w.class_topics.filter((c) => c.is_published);

      return {
        ...w,
        class_topics: publishedClassTopics.map((c) => {
          return {
            ...c,
            class_topic_contents: c.class_topic_contents.filter(
              (ctn) => ctn.is_published,
            ),
          };
        }),
      };
    });
  },
);

// and sort sequence
export const selectWeekClassTopicsCurriculumWithSequence = createSelector(
  (state: RootState, class_id: number | string | undefined) =>
    selectCurriculumPublished(state, class_id),
  (weeks) => {
    const orderedWeeks = _.orderBy(weeks, 'sequence');

    return orderedWeeks.map((w) => {
      const orderedClassTopics = sortTopics(w.class_topics);

      return {
        ...w,
        class_topics: orderedClassTopics.map((class_topic) => {
          return {
            ...class_topic,
            class_topic_contents: _.orderBy(
              class_topic.class_topic_contents,
              'sequence',
            ),
          };
        }),
      };
    });
  },
);

/**
 * Add topic_contents into class topic, by check the class_topic_id of content is equal classTopic.id
 *
 * */
export const selectClassTopicsWithContent = createSelector(
  selector.selectAll,
  classTopicContentSelector.selectAll,
  (classTopics, contents) => {
    return classTopics.map((classTopic) => {
      const ref = {
        ...classTopic,
        class_topic_contents: [],
      } as Model.ClassTopic;

      contents.forEach((content) => {
        if (content.class_topic_id === classTopic.id) {
          ref.class_topic_contents.push(content);
        }
      });

      return ref;
    });
  },
);

/**
 * Map week to sider, also make assignment_content become a last child of class_topic
 * */
export const selectWeekClassTopicSidebar = createSelector(
  (state: RootState) => state.ui.app.me?.id,
  (state: RootState, class_id: number | string | undefined) =>
    selectWeekClassTopicsCurriculumWithSequence(state, class_id),
  (state: RootState, class_id: number | string | undefined) =>
    classByIdSelector(state, String(class_id)),
  (user_id, weeks, course) => {
    return weeks.map((w) => {
      return {
        key: w.id,
        content: w.name,
        is_feedback_enabled: w.is_feedback_enabled,
        children: w.class_topics?.map((class_topic) => {
          let content: MenuData[] = [];

          const classTopicContents: MenuData[] =
            class_topic.class_topic_contents?.map((content) => {
              return {
                key: `${class_topic.id}/${content.id}`,
                content: content.file_name,
                completed: content.class_topic_content_completion?.is_completed,
              };
            }) || [];

          // homework content
          const homework =
            (course?.type || class_topic.class?.type) !== 'CISL' &&
            class_topic.assignment_content;

          const homeworkContent = homework
            ? [
                {
                  key: `${class_topic.id}/homework`,
                  content:
                    class_topic?.class?.type === 'LAUNCHPAD'
                      ? 'Homework'
                      : 'Assignment',
                  completed: class_topic.has_submitted_homeworks,
                },
              ]
            : [];

          // module quiz content
          const moduleQuizContent = class_topic.total_questions
            ? [
                {
                  key: `${class_topic.id}/module_quiz`,
                  content: 'Module Quiz',
                  completed: class_topic.has_submitted_questions,
                },
              ]
            : [];

          // content completed list
          const allContents = content.concat(
            classTopicContents,
            homeworkContent,
            moduleQuizContent,
          );
          const contentsCompleted = allContents.filter((c) => c.completed);

          return {
            key: class_topic.id,
            content: class_topic.topic.name,
            children: allContents,
            no_contents_completed: contentsCompleted.length,
            completed: contentsCompleted.length === allContents.length,
          };
        }),
      };
    }) as MenuData[];
  },
);

export const selectStepContentList = createSelector(
  selectWeekClassTopicSidebar,
  (state: RootState, class_id?: string, pathname?: string) => ({
    pathname,
    class_id,
  }),
  (weeks, { pathname, class_id }) => {
    const classTopicListWithFeedback = weeks.flatMap((w) =>
      w.children?.concat(
        w.is_feedback_enabled
          ? {
              key: w.key,
              children: [{ key: `${w.key}/feedback` }],
            }
          : [],
      ),
    );

    const contentList = classTopicListWithFeedback.flatMap(
      (w) => w?.children,
    ) as MenuData[];

    const key = pathname
      ?.split('/')
      .filter((txt) => !['', 'class', class_id].includes(txt))
      .join('/');

    const currentIndex = contentList.findIndex((c) => c.key === key);

    if (currentIndex === -1) return {};

    const nextContent = contentList[currentIndex + 1];
    const currentContent = contentList[currentIndex];
    const prevContent = contentList[currentIndex - 1];

    let stepSaveToServer: Key = nextContent?.key || currentContent.key;
    // check the key is feedback or not
    if (nextContent?.key?.toString()?.endsWith('feedback')) {
      stepSaveToServer = contentList[currentIndex + 2]?.key;
    }

    return {
      next: nextContent?.key,
      back: prevContent?.key,
      stepSaveToServer,
      current: currentContent.key,
    };
  },
);

export const selectFirstContent = createSelector(
  selectWeekClassTopicSidebar,
  (weeks) => {
    const contentList = weeks.flatMap((w) =>
      w.children?.flatMap((x) => x.children),
    ) as MenuData[];

    return contentList[0]?.key;
  },
);

export const selectContentByIdWithClassTopic = createSelector(
  selectClassTopicsWithContent,
  (state: RootState, class_topic_content_id: string | undefined) =>
    classTopicContentSelector.selectById(state, class_topic_content_id!),
  (class_topics, class_topic_content) => {
    const class_topic_id = class_topic_content?.class_topic_id;

    return {
      ...class_topic_content,
      class_topic: class_topics.find(
        (class_topic) => class_topic.id === class_topic_id,
      ),
    };
  },
);

export const selectUserAssignmentFileByClassTopicAndUser = createSelector(
  userAssignmentFileSelector.selectAll,
  (state: RootState) => state.ui.app.me?.id,
  (state: RootState, class_topic_id: string) =>
    selector.selectById(state, class_topic_id),
  (user_assignment_files, user_id, class_topic) => {
    return user_assignment_files.filter(
      (file) =>
        file.class_topic_id === class_topic?.id && file.user_id === user_id,
    );
  },
);

export const selectUserAssignmentFileByClassTopic = createSelector(
  userAssignmentFileSelector.selectAll,
  (state: RootState, class_topic_id: string) =>
    selector.selectById(state, class_topic_id),
  (state: RootState, class_topic_id: string, user_id?: string) => user_id,
  (user_assignment_files, class_topic, user_id) => {
    const fileByIds = _.groupBy(
      user_assignment_files.filter(
        (file) => file.class_topic_id === class_topic?.id,
      ),
      'user_id',
    );

    return user_id ? fileByIds[user_id] || [] : Object.values(fileByIds) || [];
  },
);

export const selectProgress = createSelector(
  selectClassUserByClassId,
  selectWeekClassTopicSidebar,
  (class_user, weeks) => {
    if (!class_user) return { progress: 0 };
    const { expired_date } = getExpiredDate(class_user);

    const totalContents = weeks.flatMap((w) =>
      w.children?.flatMap((c) => c.children),
    );
    const totalContentCompleted = totalContents.filter((c) => c?.completed);

    const progress =
      totalContents.length === 0
        ? 0
        : Math.round(
            (totalContentCompleted.length / totalContents.length) * 100,
          );

    return {
      progress: progress || class_user.class_progress?.percentage,
      class_user,
      expired_date,
    };
  },
);

export const selectIssueType = createSelector(
  (state: RootState) => userCoursesSelector(state, 'my_courses'),
  (
    state: RootState,
    class_id: string | undefined,
    type: Model.UserIssue['type'],
    type_id: string | undefined,
  ) => ({
    class_id,
    type,
    state,
    type_id,
  }),
  (_, { class_id, state, type, type_id }) => {
    if (!class_id) return [];

    const weeks = selectWeekClassTopicSidebar(state, class_id);

    if (type === 'others') {
      return [];
    }

    return weeks.map((w) => {
      return {
        type: 'week_topic_id',
        label: w.content,
        value: w.key,
        isDefaultValue: type_id === w.key,
        children:
          w.children?.map((class_topic) => {
            return {
              type: 'class_topic_id',
              label: class_topic.content,
              value: class_topic.key,
              isDefaultValue: type_id === class_topic.key,
              children:
                class_topic.children?.map((c) => {
                  return {
                    type: 'class_topic_content_id',
                    label: c.content,
                    value: c.key,
                    isDefaultValue: type_id === c.key,
                  };
                }) || [],
            };
          }) || [],
      };
    });
  },
);

export const selectContentHierarchy = createSelector(
  selector.selectAll,
  classTopicContentSelector.selectAll,
  (
    state: RootState,
    params: {
      class_id?: string;
      type: Model.UserIssue['type'];
      searchValue?: string;
    },
  ) => {
    return params;
  },
  (
    class_topics,
    class_topic_contents,
    { type, class_id, searchValue = '' },
  ) => {
    const re = new RegExp(_.escapeRegExp(searchValue), 'i');
    const isMatch = (result) => re.test(result.title);

    if (type === 'class_topic_id') {
      const results = class_topics
        .filter((ct) => ct.class_id === class_id)
        .map((ct) => {
          return {
            title: ct.topic?.name,
            id: ct.id,
          };
        });

      return searchValue.length < 1 ? results : _.filter(results, isMatch);
    }
    if (type === 'class_topic_content_id') {
      const results = class_topic_contents
        .filter((ctc) => ctc.class_topic?.class_id === class_id)
        .map((ctc) => {
          return {
            title: ctc.file_name,
            id: ctc.id,
          };
        });

      return searchValue.length < 1 ? results : _.filter(results, isMatch);
    }

    return [];
  },
);

export const selectShouldContentBlock = createSelector(
  selectWeekClassTopicSidebar,
  (
    state: RootState,
    class_id?: string,
    current?: string | number,
    pathname?: string,
  ) => ({
    pathname,
    class_id,
    current,
  }),
  (weeks, { pathname, class_id, current }) => {
    const modules = weeks.flatMap((w) => w.children);
    const selectedKey = pathname
      ?.split('/')
      .filter((text) => !['', PATH.CLASS, class_id].includes(text))
      .join('/');

    const currentModule = modules.find((m) =>
      m?.children?.some((c) => c.key === current),
    );
    const selectedModule = modules.find((m) =>
      m?.children?.some((c) => c.key === selectedKey),
    );
    const selectedModuleIndex = modules.findIndex(
      (m) => m?.key === selectedModule?.key,
    );
    // return false means no block

    // for feedback item
    if (selectedKey?.endsWith('feedback')) {
      const week_id = selectedKey?.split('/')[0];

      const currentWeek = weeks.find((w) => w.key === week_id);

      return !currentWeek?.children?.every((c) => c.completed);
    }

    if (selectedKey === current) return false;

    if (currentModule && selectedModule) {
      const selectedModuleHasItemCompleted = selectedModule.children?.some(
        (c) => c.completed,
      );

      if (selectedModuleHasItemCompleted) return false;

      const latestModuleCompletedIndex = _.findLastIndex(
        modules,
        (m) => m?.completed,
      );

      if (latestModuleCompletedIndex === -1 && selectedModuleIndex === 0)
        return false;
      if (latestModuleCompletedIndex + 1 === selectedModuleIndex) return false;
      if (latestModuleCompletedIndex > selectedModuleIndex) return false;

      return true;
    }

    return false;
  },
);

export { reducer };
export default actions;
